/**
 * "May developer debug surfaces run in this build?" — one answer, shared by
 * every diagnostic overlay and recorder, so they can never disagree.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE FOOTGUN THIS EXISTS TO AVOID: `NODE_ENV` does NOT mean "prod site".
 * ─────────────────────────────────────────────────────────────────────────
 * As [`supabaseConfig.js`](../../config/supabaseConfig.js) says in its first
 * comment: **Vercel always builds with `NODE_ENV=production`.** So on
 * `app-dev.crewvex.com` — a full Vercel Production deploy in its own
 * project — `NODE_ENV === 'production'`, exactly like the real prod site.
 *
 * A debug flag written as `NODE_ENV !== 'production'` would therefore be dead
 * on the DEV SITE too, surviving only on `lvh.me:3000`. That is the worst
 * possible outcome for these particular surfaces: the incidents they exist to
 * diagnose are **iOS-PWA-only** (dropped `animationend`, stale
 * `visualViewport`, Vaul tap-eating, the keyboard pan law), which means they
 * are observed on a PHONE, installed from the dev site — never on localhost,
 * and never in CI or jsdom. Gating on `NODE_ENV` would delete the instrument
 * from the only place it is ever used.
 *
 * The right discriminator is the one the app already trusts to pick its
 * Supabase project: `REACT_APP_SUPABASE_ENV=development`, set on the Vercel
 * DEV project. So "debug build" = localhost OR the dev deployment.
 *
 * OPERATIONAL DEPENDENCY: if `REACT_APP_SUPABASE_ENV` is ever unset on the
 * dev Vercel project, debug goes quiet there with no other symptom. The tell
 * already exists — [`validateEnv`](./validateEnv.js) `console.warn`s on any
 * production build missing that var — so the dev site's browser console will
 * say so. (It would also be pointing at PROD Supabase at that moment, which
 * is the much louder problem.)
 *
 * Written as a direct `process.env` expression rather than through the helper
 * below so CRA's DefinePlugin substitutes literals and terser folds it to a
 * constant in a prod build. That does NOT by itself strip the debug modules
 * from `main.js` (the repo declares no `sideEffects`, so webpack must assume
 * every module has side effects and cannot drop one whose exports are still
 * imported) — this flag makes debug UNREACHABLE in prod, not absent. Actually
 * removing the ~20KB would need the lazy-chunk treatment the playbook uses for
 * Sentry / BulkRunDock / the i18n catalogs; deliberately not done here.
 */

/**
 * Pure form of the same rule, for exhaustive testing. Kept in LOCKSTEP with
 * the constant below by `debugBuild.test.js` — same discipline as
 * `requireAal2.ts` ↔ `aalClaim.js`. If you change one, change both.
 *
 * @param {string|undefined} nodeEnv `process.env.NODE_ENV`
 * @param {string|undefined} supabaseEnv `process.env.REACT_APP_SUPABASE_ENV`
 * @returns {boolean}
 */
export function computeIsDebugBuild(nodeEnv, supabaseEnv) {
  return nodeEnv !== 'production' || supabaseEnv === 'development';
}

/** True on localhost and on the dev deployment; FALSE on production. */
export const IS_DEBUG_BUILD = process.env.NODE_ENV !== 'production'
  || process.env.REACT_APP_SUPABASE_ENV === 'development';
